import React, { FC, useMemo, useState } from "react";
import { SyncOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { FormBaseType } from "@green0meter/api-client";
import { enqueueSnackbar } from "notistack";

import {
  StyledListItemIcon,
  StyledListItemText,
  StyledMenuItem,
} from "../../ActionsDropdown.styled";

import { useGetAvailableForms, useSyncForm } from "@api";
import { useUserStore } from "@store/user";
import { useMultistepFormStore } from "@components/common/MultistepForm/MultistepForm.store";
import { Chip, ConfirmationModal } from "@components/common";
import { Dropdown, MenuOption } from "@components/base";

const Synchronize: FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState<MenuOption>();
  const { t } = useTranslation();
  const { user } = useUserStore();
  const {
    formSettings: {
      formBasics: { id },
    },
  } = useMultistepFormStore();
  const { syncFormAsync } = useSyncForm(user, id);
  const { availableForms } = useGetAvailableForms(
    user,
    Object.values(FormBaseType).filter(
      (value) => typeof value === "number",
    ) as FormBaseType[],
  );

  const formSelectorOptions = useMemo(() => {
    return (
      availableForms
        ?.filter((form) => form.id !== id)
        .map((form) => ({
          title: form.name,
          id: form.id.toString(),
        })) ?? []
    );
  }, [availableForms]);

  const handleOpenModal = (): void => {
    setModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setModalOpen(false);
  };

  const handleSelectForm = (form: MenuOption): void => {
    setSelectedForm(form);
  };

  const handleSync = async (): Promise<void> => {
    if (selectedForm) {
      setIsLoading(true);

      try {
        await syncFormAsync({
          id,
          syncForm: {
            sourceFormId: selectedForm.id.toString(),
          },
        });

        setModalOpen(false);

        enqueueSnackbar(t("forms.snackbar.syncSuccess"), { variant: "success" });
      } catch {
        enqueueSnackbar(t("forms.snackbar.syncError"), { variant: "error" });
      }

      setIsLoading(false);
    }
  };

  return (
    <>
      {formSelectorOptions.length > 0 && (
        <StyledMenuItem onClick={handleOpenModal}>
          <StyledListItemIcon>
            <SyncOutlined fontSize="medium" />
          </StyledListItemIcon>
          <StyledListItemText>{t("forms.synchronize")}</StyledListItemText>
        </StyledMenuItem>
      )}
      <ConfirmationModal
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleSync}
        isLoading={isLoading}
        title={
          <>
            <SyncOutlined fontSize="large" />
            <br />
            {t("forms.syncTitle")}
          </>
        }
        description={
          <>
            {t("forms.syncDescription")}
            {formSelectorOptions && (
              <Dropdown
                button={{
                  title: (
                    <Chip
                      label={selectedForm?.title || t("forms.syncSelectForm")}
                      type={"form"}
                      size="small"
                    />
                  ),
                  variant: "text",
                  size: "small",
                }}
                options={formSelectorOptions}
                onSelect={handleSelectForm}
              />
            )}
          </>
        }
        confirmText={t("forms.synchronize")}
      />
    </>
  );
};

export default Synchronize;
